import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { BLOCK_OPTIONS } from './blocks'
import type { BlockOption, BlockCategory } from './blocks'
import type { BlockType } from './types'

interface Props {
  query: string
  // Anchor element rect (top + bottom + left in client coords).
  // Menu opens just below `anchor.bottom`, but flips above when not enough room.
  anchor: { left: number; top: number; bottom: number }
  // When true, menu shows its own search input (used for transform mode).
  showSearch?: boolean
  onPick: (type: BlockType) => void
  onClose: () => void
}

const CATEGORY_ORDER: BlockCategory[] = ['Basic', 'Layout', 'Content', 'Media', 'Action', 'Form', 'App', 'eCommerce']
const MENU_GAP = 6
const MIN_VIEWPORT_MARGIN = 8

export default function SlashMenu({ query, anchor, showSearch, onPick, onClose }: Props) {
  // Internal search query when running in transform mode (no contenteditable feeding it)
  const [internalQuery, setInternalQuery] = useState('')
  const effectiveQuery = showSearch ? internalQuery : query

  const filtered: BlockOption[] = useMemo(() => {
    const q = effectiveQuery.toLowerCase()
    return BLOCK_OPTIONS.filter((opt) =>
      opt.label.toLowerCase().includes(q) ||
      opt.type.toLowerCase().includes(q) ||
      opt.description.toLowerCase().includes(q)
    )
  }, [effectiveQuery])

  // Group by category
  const grouped = useMemo(() => {
    const map: Record<string, BlockOption[]> = {}
    for (const o of filtered) {
      if (!map[o.category]) map[o.category] = []
      map[o.category].push(o)
    }
    return CATEGORY_ORDER.filter((c) => map[c]?.length).map((c) => ({ category: c, items: map[c] }))
  }, [filtered])

  const flat: BlockOption[] = useMemo(() => grouped.flatMap((g) => g.items), [grouped])

  const [activeIdx, setActiveIdx] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const searchRef = useRef<HTMLInputElement>(null)

  // Position state — measured after layout so we can flip up if needed
  const [position, setPosition] = useState<{ top: number; left: number; openUp: boolean }>({
    top: anchor.bottom + MENU_GAP,
    left: anchor.left,
    openUp: false,
  })

  useEffect(() => { setActiveIdx(0) }, [effectiveQuery])

  useEffect(() => {
    if (showSearch) searchRef.current?.focus()
  }, [showSearch])

  // After mount/layout, measure menu and flip up if not enough room below
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const menuHeight = el.offsetHeight
    const menuWidth = el.offsetWidth
    const vh = window.innerHeight
    const vw = window.innerWidth

    const spaceBelow = vh - anchor.bottom - MIN_VIEWPORT_MARGIN
    const spaceAbove = anchor.top - MIN_VIEWPORT_MARGIN
    const openUp = menuHeight > spaceBelow && spaceAbove > spaceBelow

    let top: number
    if (openUp) {
      top = Math.max(MIN_VIEWPORT_MARGIN, anchor.top - MENU_GAP - menuHeight)
    } else {
      top = anchor.bottom + MENU_GAP
      // If still overflows bottom, clamp it up
      if (top + menuHeight > vh - MIN_VIEWPORT_MARGIN) {
        top = Math.max(MIN_VIEWPORT_MARGIN, vh - MIN_VIEWPORT_MARGIN - menuHeight)
      }
    }

    let left = anchor.left
    if (left + menuWidth > vw - MIN_VIEWPORT_MARGIN) {
      left = Math.max(MIN_VIEWPORT_MARGIN, vw - MIN_VIEWPORT_MARGIN - menuWidth)
    }

    setPosition({ top, left, openUp })
  }, [anchor.top, anchor.bottom, anchor.left, flat.length, showSearch])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIdx((i) => Math.min(flat.length - 1, i + 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIdx((i) => Math.max(0, i - 1))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (flat[activeIdx]) onPick(flat[activeIdx].type)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey, { capture: true })
    return () => window.removeEventListener('keydown', onKey, { capture: true } as EventListenerOptions)
  }, [flat, activeIdx, onPick, onClose])

  useEffect(() => {
    const el = itemRefs.current[activeIdx]
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [activeIdx])

  // Close on outside click (transform mode)
  useEffect(() => {
    if (!showSearch) return
    const onDown = (e: MouseEvent) => {
      const el = containerRef.current
      if (el && !el.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [showSearch, onClose])

  if (flat.length === 0 && !showSearch) return null

  let runningIdx = -1

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        left: position.left,
        top: position.top,
        zIndex: 1000,
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
        minWidth: 300,
        maxWidth: 360,
        maxHeight: 420,
        overflowY: 'auto',
        padding: 4,
      }}
    >
      {showSearch && (
        <div style={{ padding: 4 }}>
          <input
            ref={searchRef}
            value={internalQuery}
            onChange={(e) => setInternalQuery(e.target.value)}
            placeholder="Search blocks…"
            style={{
              width: '100%', padding: '6px 10px', fontSize: '0.9125rem',
              border: '1px solid var(--color-border)', borderRadius: 6,
              background: 'var(--color-bg)', color: 'var(--color-text)', outline: 'none',
            }}
          />
        </div>
      )}

      {flat.length === 0 && showSearch && (
        <div style={{
          padding: '14px 12px', color: 'var(--color-text-secondary)',
          fontSize: '0.8625rem', textAlign: 'center',
        }}>No matching blocks</div>
      )}

      {grouped.map((g) => (
        <div key={g.category}>
          <div style={{
            padding: '8px 10px 4px', fontSize: '0.8125rem', fontWeight: 700,
            color: 'var(--color-text-secondary)', textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}>{g.category}</div>
          {g.items.map((opt) => {
            runningIdx += 1
            const i = runningIdx
            return (
              <button
                key={opt.type}
                ref={(el) => { itemRefs.current[i] = el }}
                onClick={() => onPick(opt.type)}
                onMouseEnter={() => setActiveIdx(i)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '6px 10px',
                  background: activeIdx === i ? 'var(--color-accent-weak)' : 'transparent',
                  border: 'none', borderRadius: 6, textAlign: 'left',
                  cursor: 'pointer', color: 'var(--color-text)',
                }}
              >
                <div style={{
                  width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 6,
                  fontSize: '0.9125rem', fontWeight: 600, flexShrink: 0,
                }}>{opt.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.9125rem', fontWeight: 500 }}>{opt.label}</div>
                  <div style={{
                    fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{opt.description}</div>
                </div>
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}
